import type { AiReviewJob } from '@zunelo/shared';
import type { IGitHubClient } from './clients/types';

export const AI_REVIEW_STATUS_RETRY_DELAYS_MS = [250, 1000, 2500] as const;
export const AI_REVIEW_STATUS_REFRESH_FAILED = 'AI_REVIEW_STATUS_REFRESH_FAILED' as const;

export interface AiReviewStatusRefreshError extends Error {
  code: typeof AI_REVIEW_STATUS_REFRESH_FAILED;
}

export interface AiReviewStatusRetryOptions {
  sleep?: (delayMs: number) => Promise<void>;
}

const defaultSleep = (delayMs: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, delayMs));

function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | undefined)?.status;
  return typeof status === 'number' && Number.isFinite(status) ? status : undefined;
}

function isTransient(error: unknown): boolean {
  const status = statusOf(error);
  if (status === undefined) return true;
  if (status === 408 || status === 425 || status === 429) return true;
  return status < 400 || status >= 500;
}

function statusRefreshError(): AiReviewStatusRefreshError {
  const error = new Error(
    'Live review status could not be refreshed. Retry status.',
  ) as AiReviewStatusRefreshError;
  error.code = AI_REVIEW_STATUS_REFRESH_FAILED;
  return error;
}

export async function getAiReviewJobWithRetry(
  client: Pick<IGitHubClient, 'getAiReviewJob'>,
  jobId: string,
  options: AiReviewStatusRetryOptions = {},
): Promise<AiReviewJob> {
  const sleep = options.sleep ?? defaultSleep;
  for (let attempt = 0; attempt <= AI_REVIEW_STATUS_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return (await client.getAiReviewJob(jobId)).data;
    } catch (error) {
      const delay = AI_REVIEW_STATUS_RETRY_DELAYS_MS[attempt];
      if (!isTransient(error) || delay === undefined) throw statusRefreshError();
      await sleep(delay);
    }
  }
  throw statusRefreshError();
}
